import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Form, Skeleton } from 'antd'
import dayjs from 'dayjs'
import {
  rentalOrderSupplementSchema,
  type RentalOrder,
  type RentalOrderSupplementInput
} from '@super-mega/server/contracts/order'
import { EnterpriseInlineSelector, type EnterpriseSelection } from '@/features/service-enterprise/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { createOrderEditSubmission, enterpriseSubmissionNotice } from './utils/order-enterprise-selection.ts'

export function RentalEnterpriseEditor({
  order,
  onSaved,
  onBusyChange
}: {
  order: RentalOrder
  onSaved?: (order: RentalOrder) => void
  onBusyChange?: (busy: boolean) => void
}) {
  const trpc = useTRPC()
  const subject = useQuery({
    ...trpc.order.subjects.detail.queryOptions({ subjectId: order.subjectId ?? 0 }),
    enabled: Boolean(order.subjectId)
  })
  if (order.subjectId && subject.isPending) return <Skeleton active />
  if (subject.error)
    return (
      <Alert
        type="error"
        title={subject.error.message}
        action={<Button onClick={() => void subject.refetch()}>重试</Button>}
      />
    )
  const initial: EnterpriseSelection | undefined = subject.data
    ? { mode: 'existing', subjectId: subject.data.id, expectedVersion: subject.data.version }
    : undefined
  return (
    <RentalEnterpriseForm
      key={order.id}
      order={order}
      initial={initial}
      onSaved={onSaved}
      onBusyChange={onBusyChange}
    />
  )
}

function RentalEnterpriseForm({
  order,
  initial,
  onSaved,
  onBusyChange
}: {
  order: RentalOrder
  initial?: EnterpriseSelection
  onSaved?: (order: RentalOrder) => void
  onBusyChange?: (busy: boolean) => void
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message, modal } = App.useApp()
  const [baseline] = useState(order)
  const [selection, setSelection] = useState<EnterpriseSelection | undefined>(initial)
  const [pending, setPending] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const [submission] = useState(() => createOrderEditSubmission<RentalOrderSupplementInput>())
  const update = useMutation(trpc.order.rental.supplementDocuments.mutationOptions())
  return (
    <Form
      className="flex h-full min-h-0 w-full flex-col"
      layout="vertical"
      onFinish={() => {
        if (saving || pending || !selection || selection.mode === 'none') {
          return Promise.resolve(false)
        }
        setSaving(true)
        onBusyChange?.(true)
        return (async () => {
          try {
            const notice = enterpriseSubmissionNotice([selection])
            if (notice && !(await modal.confirm({ title: '确认保存订单及企业资料', content: notice }))) {
              return false
            }
            const payload = await submission.prepare(selection, async () => {
              const latest = await cache.fetchQuery({
                ...trpc.order.rental.detail.queryOptions({ rentalOrderId: order.id }),
                staleTime: 0
              })
              if (
                latest.customerId !== order.customerId ||
                latest.subjectId !== baseline.subjectId ||
                !dayjs(latest.updatedAt).isSame(baseline.updatedAt)
              ) {
                throw new Error('订单已变化，请刷新页面后重新核对')
              }
              return rentalOrderSupplementSchema.parse({
                rentalOrderId: latest.id,
                expectedUpdatedAt: baseline.updatedAt,
                enterpriseSelection: selection,
                idempotencyKey: crypto.randomUUID()
              })
            })
            const saved = await update.mutateAsync(payload)
            await Promise.all([
              cache.invalidateQueries(trpc.order.rental.detail.queryFilter({ rentalOrderId: order.id })),
              cache.invalidateQueries(trpc.order.rental.list.queryFilter()),
              cache.invalidateQueries(trpc.order.subjects.list.queryFilter()),
              cache.invalidateQueries(trpc.order.subjects.options.queryFilter()),
              cache.invalidateQueries(trpc.order.subjects.detail.queryFilter()),
              cache.invalidateQueries(trpc.room.list.queryFilter()),
              cache.invalidateQueries(trpc.room.tree.queryFilter())
            ])
            submission.clear()
            onSaved?.(saved)
            void message.success('服务对象及资料已保存')
            return true
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : '保存失败，请重试')
            return false
          }
        })().finally(() => {
          setSaving(false)
          onBusyChange?.(false)
        })
      }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-6">
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
          <p className="text-content-muted m-0 text-xs leading-6">
            补齐本单及场地占用的空缺资料，不受订单状态限制。已有合同正文保持原样。
          </p>
          {error && <Alert showIcon type="error" title={error} />}
          <EnterpriseInlineSelector
            customerId={order.customerId}
            defaultKind={order.leaseAction === 'setup' ? 'prospectiveCompany' : 'registeredCompany'}
            value={selection}
            onChange={setSelection}
            onReadyChange={(ready) => setPending(!ready)}
            disabled={saving}
          />
        </div>
      </div>
      <div className="border-border bg-surface flex shrink-0 items-center justify-between gap-4 border-t pt-4">
        <span className="text-content-muted text-xs">资料核对完成后，保存到当前订单</span>
        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          disabled={pending || !selection || selection.mode === 'none'}
        >
          保存本单资料
        </Button>
      </div>
    </Form>
  )
}
